import { useRef, useState } from "react";
import { ChevronRight, Trash2, UploadCloud, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { adminFetch } from "@/hooks/use-admin-session";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type PackageDraft = {
  id?: string;
  key: string;
  name: string;
  description: string;
  basePriceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  requiresBirthDate: boolean;
  requiresPartner: boolean;
  imageUrl?: string;
};

export function NumerolojiPackageEditor({
  draft,
  setDraft,
  onClose,
  onSave,
  onDelete,
  isSaving,
  isDeleting,
}: {
  draft: PackageDraft;
  setDraft: (next: PackageDraft) => void;
  onClose: () => void;
  onSave: (payload: any) => void;
  onDelete?: () => void;
  isSaving: boolean;
  isDeleting?: boolean;
}) {
  const isEdit = Boolean(draft.id);
  const title = isEdit ? "Paketi Düzenle" : "Yeni Paket Ekle";

  const fileRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  const formDisabled = Boolean(isSaving || isDeleting || isUploadingImage);

  const uploadImage = async (file: File) => {
    setImageError(null);

    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setImageError("Dosya boyutu çok büyük (max 6MB).");
      return;
    }

    setIsUploadingImage(true);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Dosya okunamadı."));
        reader.onload = () => {
          const result = typeof reader.result === "string" ? reader.result : "";
          const comma = result.indexOf(",");
          if (comma === -1) return reject(new Error("Dosya okunamadı."));
          resolve(result.slice(comma + 1));
        };
        reader.readAsDataURL(file);
      });

      const res = await adminFetch("/api/admin/numeroloji/packages/image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contentType: file.type || "image/jpeg",
          dataBase64,
          packageId: draft.id,
          packageKey: draft.key,
        }),
      });

      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Resim yüklenemedi.");
      }

      const url = typeof json?.url === "string" ? json.url : "";
      if (!url) throw new Error("Resim URL alınamadı.");

      setDraft({ ...draft, imageUrl: url });
    } catch (err) {
      setImageError(err instanceof Error ? err.message : "Resim yüklenemedi.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const baseInput =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-50";


  return (
    <div className="rounded-3xl border border-slate-200 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/40 sm:p-6">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
          <span>Ürün Yönetimi</span>
          <ChevronRight className="h-4 w-4" />
          <span>Paketler</span>
          <ChevronRight className="h-4 w-4" />
          <span className="text-slate-900 dark:text-slate-100">{title}</span>
        </div>

        <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{title}</div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">Paket bilgilerini düzenleyin ve kaydedin.</div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {isEdit && onDelete ? (
              <Button
                variant="outline"
                className="rounded-full bg-white/80 gap-2 text-rose-600 hover:text-rose-700 dark:bg-slate-950/40"
                onClick={onDelete}
                disabled={formDisabled}
              >
                <Trash2 className="h-4 w-4" />
                Sil
              </Button>
            ) : null}

            <Button
              variant="outline"
              className="rounded-full bg-white/80 dark:bg-slate-950/40"
              onClick={onClose}
              disabled={formDisabled}
            >
              Vazgeç
            </Button>
            <Button className="rounded-full" onClick={() => onSave(draft)} disabled={formDisabled}>
              {isSaving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Genel Bilgiler</div>

            <div className="mt-5 grid gap-5">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Paket Adı</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Örn: Premium (Full)"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Açıklama</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[180px] resize-none")}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Paketin kısa açıklaması..."
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Paket Görseli</Label>

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    void uploadImage(f);
                    // allow re-uploading the same file
                    e.currentTarget.value = "";
                  }}
                />

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full bg-white/80 gap-2 dark:bg-slate-950/40"
                    onClick={() => fileRef.current?.click()}
                    disabled={formDisabled}
                  >
                    <UploadCloud className="h-4 w-4" />
                    {isUploadingImage ? "Yükleniyor..." : "Resim yükle"}
                  </Button>

                  {draft.imageUrl?.trim() ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full bg-white/80 gap-2 dark:bg-slate-950/40"
                      onClick={() => setDraft({ ...draft, imageUrl: "" })}
                      disabled={formDisabled}
                    >
                      <X className="h-4 w-4" />
                      Kaldır
                    </Button>
                  ) : null}
                </div>

                {imageError ? <div className="text-xs text-destructive">{imageError}</div> : null}

                <Input
                  className={cn(baseInput)}
                  value={draft.imageUrl ?? ""}
                  onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })}
                  placeholder="İstersen URL de yapıştırabilirsin (opsiyonel)"
                  disabled={formDisabled}
                />

                {draft.imageUrl?.trim() ? (
                  <div className="mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40">
                    <img src={draft.imageUrl} alt={draft.name || "Paket görseli"} className="h-44 w-full object-cover" />
                  </div>
                ) : null}
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Fiyat & Paket Bilgisi</div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Fiyat (₺)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="numeric"
                  value={String(draft.basePriceTry)}
                  onChange={(e) => setDraft({ ...draft, basePriceTry: Number(e.target.value || 0) })}
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">İndirimli Fiyat (₺)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="numeric"
                  value={String(draft.discountedPriceTry ?? 0)}
                  onChange={(e) => {
                    const v = Number(e.target.value || 0);
                    setDraft({ ...draft, discountedPriceTry: v > 0 ? v : undefined });
                  }}
                  placeholder="İndirim yoksa 0 bırakın"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Paket Anahtarı</Label>
                <select
                  className={cn(baseInput, "h-12")}
                  value={draft.key}
                  onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                >
                  {["normal", "premium", "pin", "isim", "iliski", "test"].map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Durum</div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Paket Aktif</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Pasif ise müşteriler satın alamaz.</div>
              </div>

              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={draft.active}
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                />
                <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
              </label>
            </div>

            <div
              className={cn(
                "mt-4 inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
                draft.active
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                  : "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300",
              )}
            >
              {draft.active ? "Yayında" : "Taslak"}
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Zorunlu Alanlar</div>

            <div className="mt-4 grid gap-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Doğum tarihi gerekli</div>
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Formda doğum tarihi sorulsun.</div>
                </div>

                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={draft.requiresBirthDate}
                    onChange={(e) => setDraft({ ...draft, requiresBirthDate: e.target.checked })}
                  />
                  <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                </label>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Partner gerekli</div>
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Formda kişi B bilgileri sorulsun.</div>
                </div>

                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={draft.requiresPartner}
                    onChange={(e) => setDraft({ ...draft, requiresPartner: e.target.checked })}
                  />
                  <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                </label>
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">İpuçları</div>

            <div className="mt-4 rounded-2xl border border-blue-600/15 bg-blue-600/5 p-4 text-sm text-slate-700 dark:border-blue-500/20 dark:text-slate-200">
              <div className="font-semibold">Öneri</div>
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                Paket adı ve açıklamayı kısa/öz tutarsanız paket kartları daha temiz görünür.
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
